import { Modal } from 'components/UI/Modal';
import { Button } from 'features/components/Button';
import { useLanguage } from 'features/hooks/useLanguage';
import { downloadFileFromUrl } from 'helpers/generic';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LanguageType } from 'types';
import { LedidiRequestError } from 'features/entry-form-v2/data/makeRequest';
import { useIsCodelistEnabledForProject } from 'features/codelist/useIsCodeListEnabled';
import { useExportSeriesMutation } from './exportSeriesMutation';

interface Props {
	onClose: () => void;
	visible: boolean;
	seriesLabel: string;
}

export const ExportSeriesModal = ({ onClose, visible, seriesLabel }: Props) => {
	const params = useParams();
	const projectId = params['projectId'] as string;
	const seriesName = params['seriesName'] as string;

	const language = useLanguage();
	const translation = DICTIONARY[language];

	const isCodelistEnabledForProject = useIsCodelistEnabledForProject();
	const exportSeriesMutation = useExportSeriesMutation();

	return (
		<Modal
			title={`${translation.exportSeries} ${seriesLabel}`}
			visible={visible}
			onClose={onClose}
		>
			<ExportForm
				translation={translation}
				onCloseClicked={onClose}
				isSubmitting={exportSeriesMutation.isLoading}
				isCodelistEnabled={isCodelistEnabledForProject}
				onSubmit={async data => {
					try {
						const result = await exportSeriesMutation.mutateAsync({
							projectId,
							seriesVariables: [{ seriesName }],
							...data
						});

						downloadFileFromUrl(result.signedUrl);

						onClose();
					} catch (error) {
						console.error(error);

						if (
							error instanceof LedidiRequestError &&
							error.errors.some(e => e.code === 'error.noAccess')
						) {
							toast.error(translation.noAccessError, {
								position: 'top-center'
							});
						} else {
							toast.error(translation.genericError, {
								position: 'top-center'
							});
						}
					}
				}}
			/>
		</Modal>
	);
};

const ExportForm = ({
	translation,
	onSubmit,
	isSubmitting,
	isCodelistEnabled,
	onCloseClicked
}: {
	translation: Translation;
	onSubmit: (data: FormValues) => void;
	isSubmitting: boolean;
	isCodelistEnabled: boolean;
	onCloseClicked: () => void;
}) => {
	const form = useForm<FormValues>({
		defaultValues: {
			fileType: 'csv',
			useCategoryLabels: false,
			timezone: 'yes',
			splitMultipleCategories: false,
			includeSystemVariables: false,
			includeCodelistCustomId: false
		}
	});

	return (
		<form
			className="flex flex-col gap-10 grow self-stretch"
			onSubmit={form.handleSubmit(onSubmit)}
		>
			<fieldset>
				<legend className="text-base font-semibold">{translation.fileFormat}</legend>

				<div className="mt-4 flex gap-4">
					<label className="flex gap-1">
						<input type="radio" {...form.register('fileType')} value="csv" />
						<p className="text-base">CSV</p>
					</label>

					<label className="flex gap-1">
						<input type="radio" {...form.register('fileType')} value="xlsx" />
						<p className="text-base">Excel</p>
					</label>
				</div>
			</fieldset>

			<div className="flex gap-2 items-start">
				<input
					{...form.register('useCategoryLabels')}
					type="checkbox"
					id="useCategoryLabels"
				/>

				<label htmlFor="useCategoryLabels" className="flex flex-col gap-1">
					<p className="text-base">{translation.showCategoryLabels}</p>
					<p className="text-sm text-gray-700">{translation.showCategoryLabelsHint}</p>
				</label>
			</div>

			<fieldset>
				<legend className="text-base font-semibold">{translation.timezone}</legend>

				<div className="mt-4 flex flex-col gap-2">
					<label className="flex gap-2 items-start">
						<input
							type="radio"
							className="self-start mt-1"
							value="yes"
							{...form.register('timezone')}
						/>
						<div className="flex flex-col">
							<p className="text-base">{translation.yes}</p>
							<p className="text-sm text-gray-700">{translation.timezoneYesHint}</p>
						</div>
					</label>

					<label className="flex gap-2 items-start">
						<input
							type="radio"
							className="self-start mt-1"
							value="no"
							{...form.register('timezone')}
						/>
						<div className="flex flex-col">
							<p className="text-base">{translation.no}</p>
							<p className="text-sm text-gray-700">{translation.timezoneNoHint}</p>
						</div>
					</label>

					<label className="flex gap-2 items-start">
						<input
							type="radio"
							className="self-start mt-1"
							value="utc"
							{...form.register('timezone')}
						/>
						<div className="flex flex-col">
							<p className="text-base">UTC</p>
							<p className="text-sm text-gray-700">{translation.timezoneUtcHint}</p>
						</div>
					</label>
				</div>
			</fieldset>

			<div className="flex gap-2 items-start">
				<input
					{...form.register('splitMultipleCategories')}
					type="checkbox"
					id="splitMultipleCategories"
				/>

				<label htmlFor="splitMultipleCategories" className="flex flex-col gap-1">
					<p className="text-base">{translation.splitMultipleCategories}</p>
				</label>
			</div>

			<div className="flex gap-2 items-start">
				<input
					{...form.register('includeSystemVariables')}
					type="checkbox"
					id="includeSystemVariables"
				/>

				<label htmlFor="includeSystemVariables" className="flex flex-col gap-1">
					<p className="text-base">{translation.includeSystemVariables}</p>
					<p className="text-sm text-gray-700">
						{translation.includeSystemVariablesHint}
					</p>
				</label>
			</div>

			{isCodelistEnabled && (
				<div className="flex gap-2 items-start">
					<input
						{...form.register('includeCodelistCustomId')}
						type="checkbox"
						id="includeCodelistCustomId"
					/>

					<label htmlFor="includeCodelistCustomId" className="flex flex-col gap-1">
						<p className="text-base">{translation.includeCodelistCustomId}</p>
						<p className="text-sm text-gray-700">
							{translation.includeCodelistCustomIdHint}
						</p>
					</label>
				</div>
			)}

			<div className="flex-row-reverse flex gap-4 self-end mt-auto">
				<Button text={translation.export} loading={isSubmitting} />
				<Button
					text={translation.close}
					type="button"
					onClick={onCloseClicked}
					variant="secondary"
				/>
			</div>
		</form>
	);
};

type FormValues = {
	fileType: 'csv' | 'xlsx';
	useCategoryLabels: boolean;
	timezone: 'yes' | 'no' | 'utc';
	splitMultipleCategories: boolean;
	includeSystemVariables: boolean;
	includeCodelistCustomId: boolean;
};

type Translation = (typeof DICTIONARY)[LanguageType];

const DICTIONARY = {
	[LanguageType.English]: {
		exportSeries: 'Export series',
		fileFormat: 'File Format',
		showCategoryLabels: 'Show category labels',
		showCategoryLabelsHint:
			'Selecting this exports the exact category names or labels, like "Red", "Blue" and "Green". If not selected, either numerical or textual values will be assigned to categories. For example, "Red" might be represented as 1, "Blue" as 2, etc.',
		timezone: 'Include timezone for datetime variables?',
		timezoneYesHint: 'Datetime variables with timezone (e.g. "2021-01-01T12:00:00+01:00").',
		timezoneNoHint:
			'Datetime variables without timezone in local time (e.g. "2021-01-01 12:00:00").',
		timezoneUtcHint:
			'Datetime variables without timezone in UTC time (e.g. "2021-01-01 11:00:00").',
		splitMultipleCategories: 'Split category multiple variables into separate columns?',
		includeSystemVariables: 'Include system variables?',
		includeSystemVariablesHint:
			'System variables are variables that are not user-defined, such as entry ID, owned by, created by, etc.',
		includeCodelistCustomId: 'Include codelist ID?',
		includeCodelistCustomIdHint: "Adds the codelist record's custom ID as the first column.",
		export: 'Export',
		close: 'Close',
		yes: 'Yes',
		no: 'No',
		noAccessError: 'You do not have permission to export this series.',
		genericError: 'Failed to export series. Please try again.'
	},

	[LanguageType.Norwegian]: {
		exportSeries: 'Eksporter serie',
		fileFormat: 'Filformat',
		showCategoryLabels: 'Vis kategorilabeller',
		showCategoryLabelsHint:
			'Ved å velge dette eksporteres de eksakte kategorinavnene eller -labelene, som "Rød", "Blå" og "Grønn". Hvis ikke valgt, vil enten numeriske eller tekstlige verdier bli tilordnet kategorier. For eksempel kan "Rød" bli representert som 1, "Blå" som 2, osv.',
		timezone: 'Inkluder tidssone for dato og tid variabler?',
		timezoneYesHint: 'Dato og tid variabler med tidssone (f.eks. "2021-01-01T12:00:00+01:00").',
		timezoneNoHint:
			'Dato og tid variabler uten tidssone i lokal tid (f.eks. "2021-01-01 12:00:00").',
		timezoneUtcHint:
			'Dato og tid variabler uten tidssone i UTC-tid (f.eks. "2021-01-01 11:00:00").',
		splitMultipleCategories: 'Del multippel kategorier inn i separate kolonner?',
		includeSystemVariables: 'Inkluder systemvariabler?',
		includeSystemVariablesHint:
			'Systemvariabler er variabler som ikke er definert av brukeren, som f.eks. oppførings-ID, eid av, opprettet av, osv.',
		includeCodelistCustomId: 'Inkluder kodeliste-ID?',
		includeCodelistCustomIdHint: 'Legger til kodeliste-IDen som første kolonne.',
		export: 'Eksporter',
		close: 'Lukk',
		yes: 'Ja',
		no: 'Nei',
		noAccessError: 'Du har ikke tilgang til å eksportere denne serien.',
		genericError: 'Kunne ikke eksportere serien. Vennligst prøv igjen.'
	}
};
